import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Plus, Trash2, Pencil, Save, RotateCw, X, Eye } from "lucide-react";
import { toast } from "sonner";

const yesterdayIso = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const emptyOther = () => ({ name: "", value: "" });

const initialForm = () => ({
  tanggal: yesterdayIso(),
  sp: "",
  nsp: "",
  emergency_stock: "",
  tebal: "",
  tebal_cleaning: "",
  rb: "",
  arb: "",
  others: [emptyOther()],
});

const fmt = (n) => Number(n || 0).toLocaleString("id-ID");

export default function StockAA() {
  const [form, setForm] = useState(initialForm());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [filterTanggal, setFilterTanggal] = useState("");
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterTanggal) params.tanggal = filterTanggal;
      const { data } = await api.get("/stock-aa", { params });
      setRows(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal memuat data Stock AA");
    } finally {
      setLoading(false);
    }
  }, [filterTanggal]);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (key, value) => setForm((s) => ({ ...s, [key]: value }));

  const setOther = (idx, key, value) => {
    setForm((s) => {
      const items = [...s.others];
      items[idx] = { ...items[idx], [key]: value };
      return { ...s, others: items };
    });
  };

  const addOther = () => setForm((s) => ({ ...s, others: [...s.others, emptyOther()] }));
  const removeOther = (idx) => {
    setForm((s) => ({
      ...s,
      others: s.others.length === 1 ? [emptyOther()] : s.others.filter((_, i) => i !== idx),
    }));
  };

  const resetForm = () => {
    setForm(initialForm());
    setEditingId("");
  };

  const toPayload = () => ({
    tanggal: form.tanggal,
    sp: parseFloat(form.sp) || 0,
    nsp: parseFloat(form.nsp) || 0,
    emergency_stock: parseFloat(form.emergency_stock) || 0,
    tebal: parseFloat(form.tebal) || 0,
    tebal_cleaning: parseFloat(form.tebal_cleaning) || 0,
    rb: parseFloat(form.rb) || 0,
    arb: parseFloat(form.arb) || 0,
    others: form.others.filter((o) => o.name.trim() || o.value.trim()),
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.tanggal) {
      toast.error("Tanggal wajib diisi");
      return;
    }
    setSaving(true);
    try {
      const payload = toPayload();
      if (editingId) {
        await api.put(`/stock-aa/${editingId}`, payload);
        toast.success("Data Stock AA berhasil diupdate");
      } else {
        await api.post("/stock-aa", payload);
        toast.success("Data Stock AA berhasil ditambahkan");
      }
      resetForm();
      await load();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Gagal menyimpan data Stock AA");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row) => {
    setEditingId(row.id_stock);
    setForm({
      tanggal: row.tanggal || yesterdayIso(),
      sp: String(row.sp ?? ""),
      nsp: String(row.nsp ?? ""),
      emergency_stock: String(row.emergency_stock ?? ""),
      tebal: String(row.tebal ?? ""),
      tebal_cleaning: String(row.tebal_cleaning ?? ""),
      rb: String(row.rb ?? ""),
      arb: String(row.arb ?? ""),
      others: (row.others && row.others.length ? row.others : [emptyOther()]).map((o) => ({
        name: o.name || "",
        value: o.value || "",
      })),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const doDelete = async (row) => {
    if (!window.confirm(`Hapus data Stock AA tanggal ${row.tanggal}?`)) return;
    try {
      await api.delete(`/stock-aa/${row.id_stock}`);
      toast.success("Data Stock AA dihapus");
      if (editingId === row.id_stock) {
        resetForm();
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menghapus data");
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-[1500px] mx-auto space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <div className="label-ind">Daily Stock</div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Stock AA</h1>
          <p className="text-sm text-slate-500 mt-1">Input 1 data per tanggal. Gunakan edit untuk memperbarui data yang sudah ada.</p>
        </div>
        {editingId && (
          <Button variant="outline" onClick={resetForm} className="rounded-sm">
            <X className="w-4 h-4 mr-1" /> Batal Edit
          </Button>
        )}
      </div>

      <form onSubmit={submit}>
        <Card className="rounded-sm border-slate-200 bg-white p-4">
          <div className="space-y-2">
            <FormRow label="Tanggal">
              <Input type="date" value={form.tanggal} onChange={(e) => setField("tanggal", e.target.value)} className="h-9 rounded-sm w-[180px]" required />
            </FormRow>
            <FormRow label="SP">
              <Input type="number" step="any" value={form.sp} onChange={(e) => setField("sp", e.target.value)} className="h-9 rounded-sm font-mono w-[180px]" />
            </FormRow>
            <FormRow label="NSP">
              <Input type="number" step="any" value={form.nsp} onChange={(e) => setField("nsp", e.target.value)} className="h-9 rounded-sm font-mono w-[180px]" />
            </FormRow>
            <FormRow label="Emergency Stock">
              <Input type="number" step="any" value={form.emergency_stock} onChange={(e) => setField("emergency_stock", e.target.value)} className="h-9 rounded-sm font-mono w-[180px]" />
            </FormRow>
            <FormRow label="Tebal">
              <Input type="number" step="any" value={form.tebal} onChange={(e) => setField("tebal", e.target.value)} className="h-9 rounded-sm font-mono w-[180px]" />
            </FormRow>
            <FormRow label="Tebal Cleaning">
              <Input type="number" step="any" value={form.tebal_cleaning} onChange={(e) => setField("tebal_cleaning", e.target.value)} className="h-9 rounded-sm font-mono w-[180px]" />
            </FormRow>
            <FormRow label="RB">
              <Input type="number" step="any" value={form.rb} onChange={(e) => setField("rb", e.target.value)} className="h-9 rounded-sm font-mono w-[180px]" />
            </FormRow>
            <FormRow label="ARB">
              <Input type="number" step="any" value={form.arb} onChange={(e) => setField("arb", e.target.value)} className="h-9 rounded-sm font-mono w-[180px]" />
            </FormRow>
          </div>

          <div className="mt-4 border-t border-slate-200 pt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="label-ind">Custom</div>
              <Button type="button" variant="outline" size="sm" onClick={addOther} className="rounded-sm">
                <Plus className="w-4 h-4 mr-1" /> Tambah Baris Custom
              </Button>
            </div>
            <div className="space-y-2">
              {form.others.map((row, idx) => (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-[180px_180px_auto] gap-2">
                  <Input
                    placeholder="Nama kolom custom"
                    value={row.name}
                    onChange={(e) => setOther(idx, "name", e.target.value)}
                    className="h-9 rounded-sm w-[180px]"
                  />
                  <Input
                    placeholder="Nilai"
                    value={row.value}
                    onChange={(e) => setOther(idx, "value", e.target.value)}
                    className="h-9 rounded-sm w-[180px]"
                  />
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeOther(idx)} className="h-9 w-9 p-0 text-red-600 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <Button type="submit" disabled={saving} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white min-w-[150px]">
              <Save className="w-4 h-4 mr-1" /> {saving ? "Menyimpan..." : editingId ? "Update" : "Simpan"}
            </Button>
          </div>
        </Card>
      </form>

      <Card className="rounded-sm border-slate-200 bg-white p-4">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-3">
          <div>
            <div className="label-ind">Riwayat Input</div>
            <h3 className="text-base font-semibold text-slate-900">Data Stock AA</h3>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <div className="label-ind mb-1">Filter Tanggal</div>
              <Input type="date" value={filterTanggal} onChange={(e) => setFilterTanggal(e.target.value)} className="h-9 rounded-sm" />
            </div>
            <Button variant="outline" onClick={load} className="h-9 rounded-sm">
              <RotateCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
            <Button variant="outline" onClick={() => setFilterTanggal("")} className="h-9 rounded-sm">
              Reset
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto border border-slate-200 rounded-sm">
          <table className="w-full table-dense">
            <thead>
              <tr>
                <th className="text-left">Tanggal</th>
                <th className="text-right">SP</th>
                <th className="text-right">NSP</th>
                <th className="text-right">Emergency</th>
                <th className="text-right">Tebal</th>
                <th className="text-right">Tebal Cleaning</th>
                <th className="text-right">RB</th>
                <th className="text-right">ARB</th>
                <th className="text-left">Others</th>
                <th className="text-right w-24">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={10} className="text-center py-8 text-slate-500">Memuat data...</td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-center py-8 text-slate-400">Belum ada data.</td>
                </tr>
              )}
              {!loading && rows.map((row) => (
                <tr key={row.id_stock}>
                  <td className="font-mono">{row.tanggal}</td>
                  <td className="text-right font-mono">{fmt(row.sp)}</td>
                  <td className="text-right font-mono">{fmt(row.nsp)}</td>
                  <td className="text-right font-mono">{fmt(row.emergency_stock)}</td>
                  <td className="text-right font-mono">{fmt(row.tebal)}</td>
                  <td className="text-right font-mono">{fmt(row.tebal_cleaning)}</td>
                  <td className="text-right font-mono">{fmt(row.rb)}</td>
                  <td className="text-right font-mono">{fmt(row.arb)}</td>
                  <td className="text-xs">
                    {row.others?.length
                      ? row.others.map((o, i) => `${o.name}: ${o.value}`).join(" | ")
                      : "-"}
                  </td>
                  <td className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => startEdit(row)}>
                        <Pencil className="w-4 h-4 text-blue-600" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setPreview(row)}>
                        <Eye className="w-4 h-4 text-slate-600" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => doDelete(row)}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detail Stock AA</DialogTitle>
            <DialogDescription>
              Ringkasan data Stock AA untuk tanggal <span className="font-mono">{preview?.tanggal || "-"}</span>
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <PreviewCell label="Tanggal" value={preview.tanggal} mono />
              <PreviewCell label="SP" value={fmt(preview.sp)} mono />
              <PreviewCell label="NSP" value={fmt(preview.nsp)} mono />
              <PreviewCell label="Emergency Stock" value={fmt(preview.emergency_stock)} mono />
              <PreviewCell label="Tebal" value={fmt(preview.tebal)} mono />
              <PreviewCell label="Tebal Cleaning" value={fmt(preview.tebal_cleaning)} mono />
              <PreviewCell label="RB" value={fmt(preview.rb)} mono />
              <PreviewCell label="ARB" value={fmt(preview.arb)} mono />
              <div className="col-span-2 md:col-span-4 border border-slate-200 rounded-sm p-2 bg-slate-50">
                <div className="label-ind mb-1">Custom</div>
                {preview.others?.length ? (
                  <div className="text-sm text-slate-700">
                    {preview.others.map((o, i) => (
                      <div key={i}>
                        <span className="font-semibold">{o.name || "-"}</span>: {o.value || "-"}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-slate-400">Tidak ada data custom.</div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FormRow({ label, children }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] md:items-center gap-2">
      <div className="label-ind">{label}</div>
      <div>{children}</div>
    </div>
  );
}

function PreviewCell({ label, value, mono }) {
  return (
    <div className="border border-slate-200 rounded-sm p-2 bg-slate-50">
      <div className="label-ind">{label}</div>
      <div className={`${mono ? "font-mono" : ""} font-semibold text-slate-900`}>{value || "-"}</div>
    </div>
  );
}
